import {
  QueryOptions,
  RequestMethods,
  useFetch,
  useMutateFetch,
  UseMutateFetchProps,
} from '@shared/fetch-utils';
import { QueryKey } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  MpeTenantHeader,
  useSelectedOrgUnit,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import { useAuth0 } from '@auth0/auth0-react';
import { handleFetchErrorWithTokenRefresh } from '@retail/utils';
import { MpeTenantKey } from '@retail/auth/types';

export interface MPEFetchError {
  timestamp: string;
  status: number;
  error: string;
  detail?: string;
  message: string;
  path: string;
}

export interface UseMPEFetchOptions {
  queryKey: QueryKey;
  url: string;
  method: RequestMethods;
  body?: any;
  token?: string;
}

export function useSelectedAppTenantHeader(): Record<typeof MpeTenantHeader, MpeTenantKey> {
  const { selectedBrand } = useUserBrands();
  return useMemo(
    () => ({
      [MpeTenantHeader]: selectedBrand.tenant.tenantAppKey,
    }),
    [selectedBrand.tenant.tenantAppKey]
  );
}

const orgUnitHeader = 'X-Org-Unit-Id';

export function useSelectedOrgUnitHeader() {
  const {
    selectedOrgUnitAssortment: { orgUnitId },
  } = useSelectedOrgUnit();
  const tenantHeader = useSelectedAppTenantHeader();
  return useMemo(
    () => ({
      [orgUnitHeader]: `${orgUnitId}`,
      ...tenantHeader,
    }),
    [orgUnitId, tenantHeader]
  );
}

export function useMPEFetch<TData, TError = MPEFetchError>(
  options: UseMPEFetchOptions & QueryOptions
) {
  const selectedOrgUnitHeader = useSelectedOrgUnitHeader();
  const { getAccessTokenSilently } = useAuth0();

  const optionsWithHeader = useMemo(
    () => ({
      ...options,
      headers: selectedOrgUnitHeader,
    }),
    [options, selectedOrgUnitHeader]
  );
  return useFetch<TData, TError>({
    ...optionsWithHeader,
    onRejected: handleFetchErrorWithTokenRefresh(getAccessTokenSilently, options),
  });
}

export function useMutateMPEFetch<ResultT, BodyT = unknown, URLVariables = any>(
  options: UseMutateFetchProps<ResultT, BodyT, URLVariables>
) {
  const selectedOrgUnitHeader = useSelectedOrgUnitHeader();
  const { getAccessTokenSilently } = useAuth0();

  return useMutateFetch<ResultT, BodyT, URLVariables>({
    headers: selectedOrgUnitHeader,
    onRejected: handleFetchErrorWithTokenRefresh(getAccessTokenSilently, options),
    ...options,
  });
}
